import styled from 'styled-components';
import { FlexColumn } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { QualityDimensions } from '../../types';
import QualityDimensionScore from './QualityDimensionScore';

const Grid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(30%, 1fr));
  grid-template-rows: 1fr 1fr;
  gap: 16px;

  [data-narrow] & {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
  }
`;

const Title = styled(Typography)`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

type Props = {
  qualityDimensions: QualityDimensions;
  onQualityDimensionThresholdsChange: () => void;
};

const QualityDimensionScores = ({
  qualityDimensions,
  onQualityDimensionThresholdsChange,
}: Props) => {
  const qualityDimensionScores = [
    {
      key: 'completeness',
      title: 'Completeness',
      tooltip:
        'Completeness measures if the data is sufficient to deliver meaningful inferences and decisions.',
      dimension: qualityDimensions.completeness,
    },
    {
      key: 'uniqueness',
      title: 'Uniqueness',
      tooltip:
        'Data uniqueness indicates whether or not each record only exists in the system once and there are no duplications',
      dimension: qualityDimensions.uniqueness,
    },
    {
      key: 'freshness',
      title: 'Freshness',
      tooltip:
        'Freshness is an indication of whether or not the data is updated according to the expected SLA',
      dimension: qualityDimensions.freshness,
    },
    {
      key: 'validity',
      title: 'Validity',
      tooltip:
        'Validity refers to whether the values are in the same data type / format / range as indicated by a predefined set of rules',
      dimension: qualityDimensions.validity,
    },
    {
      key: 'accuracy',
      title: 'Accuracy',
      tooltip:
        'Data accuracy is the level to which data represents the real-world scenario and confirms with a verifiable source. Accuracy of data ensures that the associated real-world entities can participate as planned',
      dimension: qualityDimensions.accuracy,
    },
    {
      key: 'consistency',
      title: 'Consistency',
      tooltip:
        'Consistency is a data quality dimension that measures the degree to which data is the same across all instances of the data.',
      dimension: qualityDimensions.consistency,
    },
  ];

  return (
    <FlexColumn gap="20px">
      <Title>Quality Dimension Scores</Title>

      <Grid>
        {qualityDimensionScores.map(({ key, title, tooltip, dimension }) => (
          <QualityDimensionScore
            key={key}
            title={title}
            tooltip={tooltip}
            score={dimension.score?.percent}
            tests={dimension.tests.total}
            monitoredTables={dimension.tables.monitored}
            thresholdLevel={dimension.score.value}
            scores={dimension.scoreOverTime}
            low={dimension.score.thresholds.low}
            medium={dimension.score.thresholds.medium}
            testResultsTableColumnKey={key}
            onQualityDimensionThresholdsChange={
              onQualityDimensionThresholdsChange
            }
          />
        ))}
      </Grid>
    </FlexColumn>
  );
};

export default QualityDimensionScores;
